import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import { irrigationDevicesApi } from '@/lib/api/irrigation-devices-api'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { formatApiError } from '@/lib/format-api-error'
import { formatTimeVN } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'

/** Mỗi lần tải tốn 1 + N lượt gọi SmartLab (API danh sách không có trạng thái
 *  bơm), nên làm mới 30 giây/lần thay vì 5–10 giây như tài liệu cho phép. */
export const IRRIGATION_DEVICES_REFRESH_MS = 30_000

/** Thiết bị báo trạng thái bơm sau vài giây, nên chờ rồi mới tải lại. */
export const IRRIGATION_PUMP_RECHECK_MS = 3_000

export const irrigationDevicesKeys = {
    all: ['irrigation-devices'] as const,
    runs: (deviceId: string) => ['irrigation-runs', deviceId] as const,
}

/**
 * `enabled: false` để màn khác (vd Danh sách camera lấy tên trạm) không gọi
 * API khi người dùng không có quyền xem bộ tưới. Mặc định vẫn gọi.
 *
 * `live: false` cho màn chỉ cần TÊN trạm: tải một lần, không tự làm mới. Mỗi
 * lần tải là một lượt gọi SmartLab lấy danh sách kèm trạng thái bơm từng trạm,
 * tự làm mới 30 giây chỉ để hiện tên là tải thừa lên đối tác.
 */
export function useIrrigationDevices(
    options: { enabled?: boolean; live?: boolean } = {},
) {
    const live = options.live ?? true
    return useQuery({
        queryKey: irrigationDevicesKeys.all,
        enabled: options.enabled ?? true,
        queryFn: async () => {
            const res = await irrigationDevicesApi.list()
            return res.data ?? []
        },
        refetchInterval: live ? IRRIGATION_DEVICES_REFRESH_MS : false,
        staleTime: live ? undefined : Infinity,
        refetchOnWindowFocus: live,
        // Tích hợp tắt hay tài khoản sai thì thử lại cũng vô ích.
        retry: false,
    })
}

function toastError(err: unknown, fallback: string) {
    const payload = formatApiError(err, fallback)
    toast.error(payload.title, { description: payload.description })
}

/** Sau một lệnh: tải lại phiên và danh sách bộ tưới NGAY (để nút Bật hiện
 *  đếm ngược 1 phút liền sau lệnh), rồi tải lại danh sách lần nữa khi thiết bị
 *  kịp báo trạng thái bơm. */
function useRefreshAfterCommand() {
    const qc = useQueryClient()
    return (deviceId: string) => {
        void qc.invalidateQueries({
            queryKey: irrigationDevicesKeys.runs(deviceId),
        })
        void qc.invalidateQueries({ queryKey: irrigationDevicesKeys.all })
        setTimeout(() => {
            void qc.invalidateQueries({ queryKey: irrigationDevicesKeys.all })
        }, IRRIGATION_PUMP_RECHECK_MS)
    }
}

/** Tắt bơm THẬT ngay. Luôn được phép (không có thời gian nghỉ với lệnh tắt).
 *  Không tự thử lại. */
export function useStopIrrigationPump() {
    const refresh = useRefreshAfterCommand()
    return useMutation({
        mutationFn: async (device: IrrigationDevice) => {
            const res = await irrigationDevicesApi.stop(device.id)
            return res.data
        },
        retry: false,
        onSuccess: (_res, device) => {
            toast.success(m.irrigation_pump_sent_off({ name: device.name }))
            refresh(device.id)
        },
        onError: (err) => toastError(err, m.irrigation_pump_send_error()),
    })
}

export interface CreateRunVars {
    device: IrrigationDevice
    /** ISO; null = tưới ngay. */
    startAt: string | null
    durationMinutes: number
}

/** Tưới ngay (bật bơm THẬT) hoặc hẹn giờ một lần. Không bao giờ tự gửi lại:
 *  gửi lặp lệnh bật có thể chạy bơm hai lần (tài liệu SmartLab mục 3.8). */
export function useCreateIrrigationRun() {
    const refresh = useRefreshAfterCommand()
    return useMutation({
        mutationFn: async ({
            device,
            startAt,
            durationMinutes,
        }: CreateRunVars) => {
            const res = await irrigationDevicesApi.createRun(device.id, {
                start_at: startAt,
                duration_minutes: durationMinutes,
            })
            return res.data
        },
        retry: false,
        onSuccess: (run, { device }) => {
            const name = device.name
            if (!run) return
            if (run.status === 'skipped') {
                // Phiên tạo được nhưng bơm từ chối bật: nói rõ vì sao.
                toast.error(m.irrigation_run_skipped({ name }), {
                    description:
                        resolveApiErrorMessage(run.skip_reason ?? undefined) ??
                        undefined,
                })
                refresh(device.id)
                return
            }
            if (run.status === 'planned') {
                toast.success(
                    m.irrigation_run_booked({
                        name,
                        time: formatTimeVN(run.planned_start, true),
                    }),
                )
            } else {
                toast.success(
                    m.irrigation_run_started({
                        name,
                        time: formatTimeVN(run.planned_end),
                    }),
                )
            }
            refresh(device.id)
        },
        onError: (err, { device }) => {
            toastError(err, m.irrigation_run_error())
            if (
                err instanceof ApiError &&
                err.code === 'IRRIGATION_PUMP_COOLDOWN'
            ) {
                refresh(device.id)
            }
        },
    })
}

export interface RenameDeviceVars {
    device: IrrigationDevice
    /** Đã trim; rỗng = dùng lại tên gốc SmartLab. */
    name: string
}

/** Đổi tên hiển thị của trạm trong ERP. Không chạm tới bơm. */
export function useRenameIrrigationDevice() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async ({ device, name }: RenameDeviceVars) => {
            await irrigationDevicesApi.rename(device.id, { name })
        },
        onSuccess: (_res, { device, name }) => {
            toast.success(
                name
                    ? m.irrigation_rename_saved({ name })
                    : m.irrigation_rename_restored({
                          name: device.smartlab_name,
                      }),
            )
            void qc.invalidateQueries({ queryKey: irrigationDevicesKeys.all })
        },
        onError: (err) => toastError(err, m.irrigation_rename_error()),
    })
}
